import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { useMount } from "react-use";

import InvoiceCard from "@/components/features/Invoices/components/InvoiceCard/InvoiceCard";
import { PaginatedList } from "@/components/templates";
import { InvoiceCategory, InvoicePageState } from "@/shared/domain";
import { useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import InvoiceTable from "../components/InvoiceTable/InvoiceTable";
import useInvoicesQueries from "../useInvoicesQueries";
import { getAvailableFilters } from "./utils";

const InvoicePage: FC = () => {
    const {
        state: { filterId },
    } = useRouterHook<InvoicePageState>();
    const { t } = useTranslation();
    const { incUsageCount } = useUserFeedback();
    const { navToRoot, navToInvoicesSearch } = useNavigation();

    const { tableMode, useInvoicesPageQuery } = useInvoicesQueries();

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("invoices", "Invoices");
    });

    const { total: invoicesPaidTotal } = useInvoicesPageQuery({
        defaultCategory: InvoiceCategory.PAID,
    });

    const { total: invoicesUnpaidTotal } = useInvoicesPageQuery({
        defaultCategory: InvoiceCategory.NOT_PAID,
    });

    const { total: invoicesOthersTotal } = useInvoicesPageQuery({
        defaultCategory: InvoiceCategory.OTHERS,
    });

    const {
        page,
        setPage,
        category,
        isFetching,
        setCategory,
        totalOfPages,
        result: invoices,
        handlePageSizeChange,
        orderBy,
        handleSort,
    } = useInvoicesPageQuery({ defaultCategory: filterId || InvoiceCategory.ALL });

    const renderItem = (invoice: MyBrokerApi.Invoice) => <InvoiceCard key={invoice.id} invoice={invoice} />;

    const renderTable = (items: MyBrokerApi.Invoice[]) => (
        <InvoiceTable invoices={items} orderBy={orderBy} handleSort={handleSort} />
    );

    return (
        <PaginatedList<MyBrokerApi.Invoice>
            page={page}
            tableMode={tableMode}
            emptyListMessage={t("Invoices.Empty")}
            pageTitle={t("PageTitles.Invoices")}
            renderItem={renderItem}
            renderTable={renderTable}
            isFetchingList={isFetching}
            isLoading={isFetching}
            setPage={setPage}
            category={category}
            totalOfPages={totalOfPages}
            availableFilters={getAvailableFilters(invoicesPaidTotal, invoicesUnpaidTotal, invoicesOthersTotal, t)}
            setCategory={setCategory}
            items={invoices}
            handlePageSizeChange={handlePageSizeChange}
            openSearch={navToInvoicesSearch}
            displayLinkedDocumentsButton={false}
            openLinkedDocuments={() => navToRoot()}
        />
    );
};

export default InvoicePage;
